
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Cookie, Shield, Settings, Check, X, Info } from 'lucide-react';

export function CookiePolicy() {
  const cookieTypes = [
    {
      type: 'Necessary Cookies',
      required: true,
      icon: Shield,
      color: 'bg-green-100 text-green-800 border-green-200',
      description: 'Essential for website functionality and cannot be disabled.',
      examples: [
        'Authentication tokens (auth-token)',
        'Session management (supabase.auth.token)',
        'Security cookies (CSRF protection)',
        'Load balancing cookies'
      ],
      retention: 'Session or until logout',
      purpose: 'Essential website functionality'
    },
    {
      type: 'Analytics Cookies',
      required: false,
      icon: Info,
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      description: 'Help us understand how users interact with our website.',
      examples: [
        'Google Analytics (_ga, _gid)',
        'User behavior tracking',
        'Performance metrics',
        'Feature usage statistics'
      ],
      retention: '2 years',
      purpose: 'Website analytics and improvement'
    },
    {
      type: 'Marketing Cookies',
      required: false,
      icon: Settings,
      color: 'bg-purple-100 text-purple-800 border-purple-200',
      description: 'Used to deliver personalized advertisements and measure effectiveness.',
      examples: [
        'Advertising tracking pixels',
        'Conversion tracking',
        'Retargeting cookies',
        'Social media integration'
      ],
      retention: '1 year',
      purpose: 'Personalized marketing and advertising'
    },
    {
      type: 'Preference Cookies',
      required: false,
      icon: Cookie,
      color: 'bg-orange-100 text-orange-800 border-orange-200',
      description: 'Remember your settings and preferences for a better experience.',
      examples: [
        'Language preferences',
        'UI theme settings',
        'Dashboard layout preferences',
        'Notification settings'
      ],
      retention: '1 year',
      purpose: 'Enhanced user experience'
    }
  ];

  const handleManageCookies = () => {
    // This would trigger the cookie consent modal
    window.dispatchEvent(new CustomEvent('show-cookie-settings'));
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="space-y-8">
        <div className="text-center space-y-4">
          <Badge variant="outline" className="mb-4">
            <Cookie className="h-4 w-4 mr-2" />
            Cookie Policy
          </Badge>
          <h1 className="text-3xl font-bold">Cookie Policy</h1>
          <p className="text-muted-foreground">
            How TopMatchTalent uses cookies and similar technologies
          </p>
          <p className="text-sm text-muted-foreground">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>What Are Cookies?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              Cookies are small text files stored on your device when you visit our website. 
              They help us provide you with a better experience by remembering your preferences 
              and analyzing how you use our service.
            </p>
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                We use cookies responsibly and in compliance with GDPR and other privacy regulations. 
                You have full control over non-essential cookies.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <h2 className="text-2xl font-bold">Types of Cookies We Use</h2>
          
          {cookieTypes.map((cookieType, index) => {
            const Icon = cookieType.icon;
            return (
              <Card key={index} className="relative">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon className="h-6 w-6 text-blue-600" />
                      <CardTitle className="text-lg">{cookieType.type}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      {cookieType.required ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <Check className="h-3 w-3 mr-1" />
                          Required
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                          <X className="h-3 w-3 mr-1" />
                          Optional
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {cookieType.description}
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold mb-2 text-sm">Examples:</h4>
                      <ul className="text-sm space-y-1">
                        {cookieType.examples.map((example, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                            <span>{example}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="space-y-2">
                        <div>
                          <span className="font-semibold text-sm">Purpose: </span>
                          <span className="text-sm text-muted-foreground">{cookieType.purpose}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-sm">Retention: </span>
                          <span className="text-sm text-muted-foreground">{cookieType.retention}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Third-Party Cookies</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              We may use third-party services that set their own cookies. These include:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-semibold">Analytics Services:</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Google Analytics (performance tracking)</li>
                  <li>• Vercel Analytics (site performance)</li>
                  <li>• PostHog (user behavior analytics)</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold">Essential Services:</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Supabase (authentication & database)</li>
                  <li>• Stripe (payment processing)</li>
                  <li>• OpenAI API (AI processing)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Managing Your Cookie Preferences</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              You have several options to control cookies:
            </p>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Our Cookie Settings</h4>
                <p className="text-blue-700 text-sm mb-3">
                  Use our cookie preference center to granularly control which cookies we set.
                </p>
                <Button onClick={handleManageCookies} className="bg-blue-600 hover:bg-blue-700">
                  <Settings className="h-4 w-4 mr-2" />
                  Manage Cookie Preferences
                </Button>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Browser Settings</h4>
                <p className="text-gray-700 text-sm">
                  You can also control cookies through your browser settings:
                </p>
                <ul className="text-sm text-gray-600 mt-2 space-y-1">
                  <li>• <strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
                  <li>• <strong>Firefox:</strong> Settings → Privacy & Security → Cookies</li>
                  <li>• <strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                  <li>• <strong>Edge:</strong> Settings → Cookies and site permissions</li>
                </ul>
              </div>
            </div>

            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                Note: Disabling necessary cookies may affect website functionality. 
                Some features may not work properly without essential cookies.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Data Transfers and Storage</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold mb-2">EU Data Protection:</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Primary data processing in EU</li>
                  <li>• GDPR-compliant cookie handling</li>
                  <li>• Regular compliance audits</li>
                  <li>• Data minimization principles</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Your Rights:</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Access your cookie data</li>
                  <li>• Withdraw consent anytime</li>
                  <li>• Request data deletion</li>
                  <li>• File complaints with authorities</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              If you have questions about our cookie policy or want to exercise your rights:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold mb-2">General Inquiries:</h4>
                <p className="text-sm">Email: talent@topmatchtalent.com</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Privacy Questions:</h4>
                <p className="text-sm">Email: dpo@topmatchtalent.com</p>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" asChild>
                <a href="/privacy-policy">Privacy Policy</a>
              </Button>
              <Button variant="outline" asChild>
                <a href="/data-rights">Your Data Rights</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 